import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../api/api';
import { showConfirm } from '../../utils/confirmUtils';
import { toastError } from '../../utils/toasts';
import { Card, Button } from '../../components/ui';
import TableToolbar from '../../components/stock/TableToolbar';

const initialForm = {
  itemId: '', type: 'in', quantity: '', unitPrice: '',
  reference: '', movementDate: new Date().toISOString().split('T')[0],
  notes: '', sourceType: 'other',
};

const toolbarColumns = [
  { label: 'Date', accessor: 'date' },
  { label: 'Article', accessor: 'article' },
  { label: 'Type', accessor: 'type' },
  { label: 'Qté', accessor: 'qty' },
  { label: 'Prix unitaire', accessor: 'unitPrice' },
  { label: 'Total', accessor: 'total' },
  { label: 'Référence', accessor: 'reference' },
  { label: 'Source', accessor: 'source' },
];

export default function StockMovements() {
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  const fetchMovements = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (filter) params.type = filter;
      const res = await api.get('/stock/movements', { params });
      if (res.data.ok) setMovements(res.data.items);
    } catch (err) {
      console.error('Erreur chargement mouvements:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/stock/items', { params: { limit: 500 } });
      if (res.data.ok) setItems(res.data.items);
    } catch (err) {
      console.error('Erreur chargement articles:', err);
    }
  }, []);

  useEffect(() => {
    fetchMovements();
    fetchItems();
  }, [fetchMovements, fetchItems]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.itemId) { toastError('Veuillez sélectionner un article'); return; }
    setSaving(true);
    const payload = {
      ...form,
      quantity: parseFloat(form.quantity) || 0,
      unitPrice: parseFloat(form.unitPrice) || 0,
    };
    try {
      const res = await api.post('/stock/movements', payload);
      if (res.data.ok) {
        setMovements((prev) => [res.data.movement, ...prev]);
        setShowForm(false);
        setForm(initialForm);
      }
    } catch (err) {
      console.error('Erreur création mouvement:', err);
      toastError(err.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await showConfirm('Supprimer ce mouvement ? Les quantités seront ajustées.')) return;
    try {
      await api.delete(`/stock/movements/${id}`);
      setMovements((prev) => prev.filter((m) => m._id !== id));
    } catch (err) {
      console.error('Erreur suppression mouvement:', err);
      toastError('Erreur lors de la suppression');
    }
  };

  const exportData = useMemo(() => movements.map((m) => ({
    date: new Date(m.movementDate).toLocaleDateString('fr-TN'),
    article: m.itemId?.name || 'N/A',
    type: m.type === 'in' ? 'Entrée' : 'Sortie',
    qty: m.quantity,
    unitPrice: m.unitPrice,
    total: m.totalPrice,
    reference: m.reference || '-',
    source: m.sourceType,
  })), [movements]);

  if (loading) return <p className="text-gray-500 p-6">Chargement...</p>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Mouvements de stock</h1>
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">Tous</option>
              <option value="in">Entrées</option>
              <option value="out">Sorties</option>
            </select>
            <Button variant="primary" onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Fermer' : 'Nouveau mouvement'}
            </Button>
          </div>
        </div>

        <TableToolbar
          title="Mouvements de stock"
          columns={toolbarColumns}
          data={exportData}
          filename="mouvements_stock"
        />

        {showForm && (
          <Card className="mb-6 p-4">
            <h2 className="text-lg font-semibold mb-4">Nouveau mouvement</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Article *</label>
                <select name="itemId" value={form.itemId} onChange={handleChange} required className="w-full border rounded px-3 py-2 text-sm">
                  <option value="">Sélectionner...</option>
                  {items.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name} {item.sku ? `(${item.sku})` : ''} - Stock: {item.currentQuantity} {item.unit}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type *</label>
                <select name="type" value={form.type} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="in">Entrée</option>
                  <option value="out">Sortie</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Quantité *</label>
                <input type="text" inputMode="decimal" name="quantity" value={form.quantity} onChange={handleChange} required placeholder="0" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Prix unitaire</label>
                <input type="text" inputMode="decimal" name="unitPrice" value={form.unitPrice} onChange={handleChange} placeholder="0.000" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <input type="date" name="movementDate" value={form.movementDate} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Référence</label>
                <input type="text" name="reference" value={form.reference} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Source</label>
                <select name="sourceType" value={form.sourceType} onChange={handleChange} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="other">Autre</option>
                  <option value="purchase">Achat</option>
                  <option value="sale">Vente</option>
                  <option value="adjustment">Ajustement</option>
                  <option value="transfer">Transfert</option>
                  <option value="initial">Initial</option>
                </select>
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-3 flex gap-2">
                <Button variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Création...' : 'Créer le mouvement'}
                </Button>
                <Button variant="secondary" onClick={() => setShowForm(false)}>Annuler</Button>
              </div>
            </form>
          </Card>
        )}

        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Article</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-right px-4 py-2">Qté</th>
                  <th className="text-right px-4 py-2">Prix unitaire</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-left px-4 py-2">Référence</th>
                  <th className="text-left px-4 py-2">Source</th>
                  <th className="text-center px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-4 text-gray-500">Aucun mouvement</td></tr>
                )}
                {movements.map((m) => (
                  <tr key={m._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2">{new Date(m.movementDate).toLocaleDateString('fr-TN')}</td>
                    <td className="px-4 py-2 font-medium">{m.itemId?.name || 'N/A'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold ${m.type === 'in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {m.type === 'in' ? 'Entrée' : 'Sortie'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-bold">{m.quantity}</td>
                    <td className="px-4 py-2 text-right">{m.unitPrice.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}</td>
                    <td className="px-4 py-2 text-right">{m.totalPrice.toLocaleString('fr-TN', { style: 'currency', currency: 'TND' })}</td>
                    <td className="px-4 py-2">{m.reference || '-'}</td>
                    <td className="px-4 py-2 text-gray-600">{m.sourceType}</td>
                    <td className="px-4 py-2 text-center">
                      <Button variant="danger" size="sm" onClick={() => handleDelete(m._id)}>Suppr.</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}